import { AnvilIcon } from "@/components/icons/AnvilIcon";
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import SignaturePad from "@/components/SignaturePad";
import {
  Building2, Pencil, Save, X, Eye, EyeOff, Lock, Plus, Trash2, Upload, FileText,
  Download, Loader2, Check, ShieldCheck, Users, Sparkles, Mail, Phone, AlertTriangle,
  Search, ScrollText, Tag, Calendar,
} from "lucide-react";

const API_BASE = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

const PROFILE_FIELDS = [
  ["legal_name", "Legal name"], ["entity_type", "Entity type"], ["established", "Established"], ["state", "State"],
  ["address_line1", "Address line 1"], ["address_line2", "Address line 2"], ["phone", "Phone"], ["phone_alt", "Phone (alt)"],
  ["website", "Website"], ["federal_email", "Federal email"], ["commercial_email", "Commercial email"], ["owner_email", "Owner email"],
  ["uei", "UEI"], ["cage", "CAGE"], ["duns", "DUNS"], ["sam_status", "SAM status"], ["piee_status", "PIEE / WAWF"],
  ["bonding_capacity", "Bonding capacity"], ["annual_revenue", "Annual revenue"], ["headcount", "Headcount"],
  ["geography", "Geography"], ["primary_naics", "Primary NAICS"], ["authorized_signer", "Authorized signer"],
  ["bonding_single_job_limit", "Bonding — single-job limit"], ["bonding_aggregate_limit", "Bonding — aggregate limit"],
  ["largest_completed_project", "Largest completed project"],
];
const ARRAY_FIELDS = [
  ["naics", "NAICS portfolio"], ["certifications", "Certifications"], ["capabilities", "Capabilities"],
  ["differentiators", "Differentiators"], ["federal_agencies", "Federal agencies"],
  ["past_performance", "Past performance"], ["licenses", "Licenses"],
  ["go_no_go_rules", "Go / No-Go rules"], ["excluded_personnel", "Excluded personnel"],
];

const Card = ({ title, children, right, maxH = 560, grow = false, className = "" }) => (
  <div className={`bg-white border border-zinc-200 rounded-lg flex flex-col min-w-0 ${grow ? "flex-1" : ""} ${className}`}>
    <div className="px-4 py-3 border-b border-zinc-200 flex items-center justify-between gap-2 shrink-0">
      <span className="text-[11px] tracking-[0.1em] font-semibold text-zinc-500 uppercase truncate">{title}</span>
      <span className="shrink-0">{right}</span>
    </div>
    <div className="p-4 min-w-0 flex-1" style={{ maxHeight: grow ? undefined : maxH, overflowY: "auto", boxSizing: "border-box" }}>{children}</div>
  </div>
);

export default function CompanyFile({ embedded = false }) {
  const { user } = useAuth();
  const isAdmin = !!user;  // Dir 65 — roles flattened: any authenticated employee has full access.
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ profile: {}, about: "", secrets: {}, custom_fields: [] });
  const [revealed, setRevealed] = useState(null); // {secure, custom_fields}
  const [saving, setSaving] = useState(false);
  const [ingest, setIngest] = useState(null); // {filename, proposal, accept:{...}}
  const [uploading, setUploading] = useState(false);
  const [regenBusy, setRegenBusy] = useState(false);
  const [tab, setTab] = useState("Overview");
  const [docCategory, setDocCategory] = useState("");

  const regenerateAbout = async () => {
    setRegenBusy(true);
    try {
      const r = await api.post("/company/about/regenerate");
      setData((d) => ({ ...d, about: r.data.about }));
      if (editing) setForm((f) => ({ ...f, about: r.data.about }));
      toast.success("About narrative regenerated from your structured profile.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't regenerate."); }
    finally { setRegenBusy(false); }
  };

  const setEmp = (i, key, val) => {
    const emps = [...(form.profile.employees || [])];
    emps[i] = { ...emps[i], [key]: val };
    setForm((f) => ({ ...f, profile: { ...f.profile, employees: emps } }));
  };
  const addEmp = () => setForm((f) => ({ ...f, profile: { ...f.profile, employees: [...(f.profile.employees || []), { name: "", title: "", role: "", email: "", phone: "" }] } }));
  const removeEmp = (i) => setForm((f) => ({ ...f, profile: { ...f.profile, employees: (f.profile.employees || []).filter((_, j) => j !== i) } }));

  const load = async () => {
    const r = await api.get("/company");
    setData(r.data);
  };
  useEffect(() => { load(); }, []);

  const startEdit = () => {
    setForm({
      profile: { ...data.profile },
      about: data.about || "",
      secrets: {},
      custom_fields: (data.custom_fields || []).map((c) => ({ ...c, value: c.sensitive ? "" : (c.value || "") })),
    });
    setEditing(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      // only send secret values that were typed
      const secrets = Object.fromEntries(Object.entries(form.secrets).filter(([, v]) => v !== ""));
      const r = await api.put("/company", {
        profile: form.profile, about: form.about, secrets,
        custom_fields: form.custom_fields.map((c) => ({
          id: c.id, section: c.section, label: c.label, sensitive: !!c.sensitive,
          value: c.value, // blank sensitive value = keep existing
        })),
      });
      setData(r.data); setEditing(false); setRevealed(null);
      toast.success("About Williams Diversified saved.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed."); }
    finally { setSaving(false); }
  };

  const reveal = async () => {
    if (revealed) { setRevealed(null); return; }
    try {
      const r = await api.post("/company/reveal");
      setRevealed(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Reveal failed."); }
  };

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      if (docCategory) fd.append("category", docCategory);
      const r = await api.post("/company/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await load();
      const p = r.data.proposal || {};
      const accept = {
        fields: Object.fromEntries(Object.keys(p.fields || {}).map((k) => [k, true])),
        custom: (p.custom_fields || []).map(() => true),
        about: !!p.about_addition,
      };
      setIngest({ filename: r.data.filename, proposal: p, accept });
      toast.success("Uploaded. Review what Anvil extracted.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed."); }
    finally { setUploading(false); }
  };

  const applyIngest = async () => {
    const p = ingest.proposal, a = ingest.accept;
    const fields = Object.fromEntries(Object.entries(p.fields || {}).filter(([k]) => a.fields[k]));
    const custom = (p.custom_fields || []).filter((_, i) => a.custom[i]);
    try {
      const r = await api.post("/company/ingest/apply", {
        fields, custom_fields: custom, about_addition: a.about ? p.about_addition : "",
      });
      setData(r.data); setIngest(null);
      toast.success("Applied to the profile. Anvil is smarter about WD now.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Apply failed."); }
  };

  const deleteDoc = async (id) => {
    try { await api.delete(`/company/documents/${id}`); await load(); toast("Document removed."); }
    catch { toast.error("Couldn't remove."); }
  };

  if (!data) return <div className="p-8 text-sm text-zinc-400 font-mono">Loading…</div>;

  const cf = editing ? form.custom_fields : (data.custom_fields || []);

  return (
    <div className={embedded ? "" : "p-5 sm:p-8 max-w-[1600px] mx-auto"} data-testid="about-wd-page">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          {!embedded && <div className="h-10 w-10 bg-zinc-900 flex items-center justify-center rounded-lg"><Building2 className="h-5 w-5 text-gold" /></div>}
          <div>
            {!embedded && <h1 className="font-display font-bold text-2xl tracking-tight text-zinc-900">About Williams Diversified</h1>}
            <p className="text-sm text-zinc-500">Company knowledge hub · everything here feeds Anvil on every bid.</p>
          </div>
        </div>
        {isAdmin && !editing && (
          <div className="flex items-center gap-2">
            <a href={`${API_BASE}/company/capability-statement.pdf`} target="_blank" rel="noreferrer" data-testid="gen-cap-statement-btn"
              className="flex items-center gap-1.5 text-sm border border-zinc-300 text-zinc-700 px-4 py-2 rounded-md hover:bg-zinc-50">
              <FileText className="h-4 w-4 text-gold-dark" /> Capability Statement
            </a>
            <button onClick={startEdit} data-testid="edit-profile-btn" className="flex items-center gap-1.5 text-sm bg-zinc-900 text-white px-4 py-2 rounded-md hover:bg-zinc-800">
              <Pencil className="h-4 w-4" /> Edit
            </button>
          </div>
        )}
        {editing && (
          <div className="flex items-center gap-2">
            <button onClick={() => { setEditing(false); setRevealed(null); }} className="text-sm text-zinc-500 px-3 py-2 hover:text-zinc-800">Cancel</button>
            <button onClick={save} disabled={saving} data-testid="save-profile-btn" className="flex items-center gap-1.5 text-sm bg-emerald-600 text-white px-4 py-2 rounded-md hover:bg-emerald-500 disabled:opacity-50">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
            </button>
          </div>
        )}
      </div>

      <div className="flex items-center gap-1 mt-6 border-b border-zinc-200 overflow-x-auto" data-testid="about-tabs">
        {["Overview", "Commitments", "Employees / Team", "Company Documents", "Contracts"].map((t) => (
          <button key={t} data-testid={`about-tab-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`} onClick={() => setTab(t)}
            className={`px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 -mb-px transition-colors ${
              tab === t ? "border-gold-dark text-zinc-900" : "border-transparent text-zinc-400 hover:text-zinc-700"}`}>{t}</button>
        ))}
      </div>

      {tab === "Overview" && (
      <div className="mt-6 space-y-5" data-testid="overview-panel">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 items-stretch">
        {/* LEFT: Structured fields (tall) */}
        <Card title="Company identifiers & details" maxH={720}>
          <div className="space-y-2">
            {PROFILE_FIELDS.map(([k, label]) => (
              <div key={k} className="flex items-start justify-between gap-3 py-1 border-b border-zinc-50 last:border-0">
                <span className="text-xs text-zinc-500 pt-1.5 w-32 sm:w-36 shrink-0">{label}</span>
                {editing ? (
                  <input data-testid={`field-${k}`} value={form.profile[k] ?? ""} onChange={(e) => setForm((f) => ({ ...f, profile: { ...f.profile, [k]: e.target.value } }))}
                    className="flex-1 min-w-0 border border-zinc-300 rounded px-2 py-1 text-sm text-zinc-900 outline-none focus:border-gold-dark" />
                ) : (
                  <span className="text-sm text-zinc-900 text-right flex-1 min-w-0 break-words [overflow-wrap:anywhere]">{data.profile[k] || "—"}</span>
                )}
              </div>
            ))}
          </div>
        </Card>

        {/* RIGHT: Sensitive + About + Custom stacked to fill the identifiers height */}
        <div className="flex flex-col gap-5 min-w-0">
          <Card title="Sensitive (encrypted)" maxH={220} right={isAdmin && (
            <button onClick={reveal} data-testid="reveal-btn" className="flex items-center gap-1 text-[11px] text-zinc-500 hover:text-zinc-900">
              {revealed ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />} {revealed ? "Hide" : "Reveal"}
            </button>
          )}>
            <div className="flex items-center gap-1.5 text-[11px] text-emerald-600 mb-3"><ShieldCheck className="h-3.5 w-3.5 shrink-0" /> Fernet-encrypted at rest · admin reveal only</div>
            <div className="space-y-2">
              {Object.entries(data.secure).map(([k, v]) => (
                <div key={k} className="flex items-start justify-between gap-3 py-1 border-b border-zinc-50 last:border-0">
                  <span className="text-xs text-zinc-500 w-28 sm:w-32 shrink-0 flex items-center gap-1 pt-0.5"><Lock className="h-3 w-3 shrink-0" /> {v.label}</span>
                  {editing ? (
                    <input data-testid={`secret-${k}`} placeholder={v.present ? "•••• (unchanged)" : "enter to set"} value={form.secrets[k] ?? ""}
                      onChange={(e) => setForm((f) => ({ ...f, secrets: { ...f.secrets, [k]: e.target.value } }))}
                      className="flex-1 min-w-0 border border-zinc-300 rounded px-2 py-1 text-sm font-mono text-zinc-900 outline-none focus:border-gold-dark" />
                  ) : (
                    <span className="text-sm font-mono text-zinc-900 text-right flex-1 min-w-0 break-all" data-testid={`secret-view-${k}`}>{revealed ? (revealed.secure[k] || "—") : (v.masked || "—")}</span>
                  )}
                </div>
              ))}
            </div>
          </Card>

          <Card title="About / notes (markdown)" grow right={isAdmin && (
            <button onClick={regenerateAbout} disabled={regenBusy} data-testid="regen-about-btn"
              className="flex items-center gap-1 text-[11px] text-zinc-500 hover:text-zinc-900 disabled:opacity-50">
              {regenBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 text-gold-dark" />} Regenerate from profile
            </button>
          )}>
            {editing ? (
              <textarea data-testid="about-textarea" value={form.about} onChange={(e) => setForm((f) => ({ ...f, about: e.target.value }))} rows={8}
                placeholder="Generated from your structured profile, or write your own. Anvil never fabricates facts."
                className="w-full border border-zinc-300 rounded px-3 py-2 text-sm text-zinc-800 outline-none focus:border-gold-dark leading-relaxed" />
            ) : (
              <p className="text-sm text-zinc-700 whitespace-pre-wrap break-words [overflow-wrap:anywhere] leading-relaxed" data-testid="about-view">{data.about || "No notes yet. Click 'Regenerate from profile' to build a grounded narrative."}</p>
            )}
          </Card>

          <Card title="Authorized signature (e-sign)" maxH={340}>
            <SignaturePad
              current={data.profile?.esignature}
              signerName={(data.profile?.authorized_signer || "Nalen Williams").split(",")[0].trim()}
              signerTitle={(data.profile?.authorized_signer || "").includes(",") ? data.profile.authorized_signer.split(",").slice(1).join(",").trim() : "Chief Executive Officer"}
              isAdmin={isAdmin}
              onAdopted={() => load()}
            />
          </Card>

          <Card title="Custom fields" maxH={240} right={editing && (
            <button data-testid="add-custom-field" onClick={() => setForm((f) => ({ ...f, custom_fields: [...f.custom_fields, { section: "", label: "", value: "", sensitive: false }] }))}
              className="flex items-center gap-1 text-[11px] text-zinc-600 hover:text-zinc-900"><Plus className="h-3.5 w-3.5" /> Add</button>
          )}>
            <div className="space-y-2" data-testid="custom-fields-list">
              {cf.length === 0 && <div className="text-xs text-zinc-400">No custom fields. Add anything — it's extensible.</div>}
              {cf.map((c, i) => editing ? (
                <div key={i} className="flex items-center gap-1.5 flex-wrap" data-testid={`custom-edit-${i}`}>
                  <input value={c.section} onChange={(e) => { const a = [...form.custom_fields]; a[i] = { ...a[i], section: e.target.value }; setForm((f) => ({ ...f, custom_fields: a })); }} placeholder="Section" className="w-24 border border-zinc-300 rounded px-2 py-1 text-xs outline-none focus:border-gold-dark" />
                  <input value={c.label} onChange={(e) => { const a = [...form.custom_fields]; a[i] = { ...a[i], label: e.target.value }; setForm((f) => ({ ...f, custom_fields: a })); }} placeholder="Label" className="w-28 border border-zinc-300 rounded px-2 py-1 text-xs outline-none focus:border-gold-dark" />
                  <input value={c.value} onChange={(e) => { const a = [...form.custom_fields]; a[i] = { ...a[i], value: e.target.value }; setForm((f) => ({ ...f, custom_fields: a })); }} placeholder={c.sensitive && c.id ? "•••• (unchanged)" : "Value"} className="flex-1 min-w-[120px] border border-zinc-300 rounded px-2 py-1 text-xs outline-none focus:border-gold-dark" />
                  <button title="Sensitive (encrypt)" onClick={() => { const a = [...form.custom_fields]; a[i] = { ...a[i], sensitive: !a[i].sensitive }; setForm((f) => ({ ...f, custom_fields: a })); }} className={`p-1.5 rounded ${c.sensitive ? "bg-emerald-100 text-emerald-700" : "bg-zinc-100 text-zinc-400"}`}><Lock className="h-3.5 w-3.5" /></button>
                  <button onClick={() => setForm((f) => ({ ...f, custom_fields: f.custom_fields.filter((_, j) => j !== i) }))} className="p-1.5 rounded bg-zinc-100 text-zinc-400 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              ) : (
                <div key={i} className="flex items-start justify-between gap-3 py-1 border-b border-zinc-50 last:border-0" data-testid={`custom-view-${i}`}>
                  <span className="text-xs text-zinc-500 min-w-0 break-words">{c.section ? `${c.section} / ` : ""}{c.label}{c.sensitive ? " 🔒" : ""}</span>
                  <span className="text-sm text-zinc-900 text-right flex-1 min-w-0 break-words [overflow-wrap:anywhere]">{c.sensitive ? (revealed?.custom_fields?.[c.id] || c.masked || "—") : (c.value || "—")}</span>
                </div>
              ))}
            </div>
          </Card>
        </div>
        </div>

        {/* FULL WIDTH: Capabilities & portfolio (wide: NAICS grid + capabilities + differentiators) */}
        <Card title="Capabilities & portfolio" maxH={440}>
          <div className="space-y-3">
            {ARRAY_FIELDS.map(([k, label]) => (data.profile[k]?.length ? (
              <div key={k}>
                <div className="text-[11px] uppercase tracking-wider text-zinc-400 mb-1">{label}</div>
                <div className="flex flex-wrap gap-1.5">
                  {data.profile[k].map((x, i) => (
                    <span key={i} className="text-[11px] bg-zinc-100 text-zinc-700 px-2 py-0.5 rounded-sm max-w-full break-words [overflow-wrap:anywhere]">{typeof x === "object" ? (x.name || JSON.stringify(x)) : x}</span>
                  ))}
                </div>
              </div>
            ) : null))}
          </div>
        </Card>
      </div>
      )}

      {tab === "Employees / Team" && (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mt-6 items-start">
        {/* Employees / Team */}
        <Card title="Employees / Team" right={editing && (
          <button data-testid="add-employee" onClick={addEmp} className="flex items-center gap-1 text-[11px] text-zinc-600 hover:text-zinc-900"><Plus className="h-3.5 w-3.5" /> Add</button>
        )}>
          <div className="space-y-2" data-testid="employees-list">
            {(editing ? (form.profile.employees || []) : (data.profile.employees || [])).map((e, i) => editing ? (
              <div key={i} className="flex items-center gap-1.5 flex-wrap" data-testid={`employee-edit-${i}`}>
                <input value={e.name || ""} onChange={(ev) => setEmp(i, "name", ev.target.value)} placeholder="Name" className="w-32 border border-zinc-300 rounded px-2 py-1 text-xs outline-none focus:border-gold-dark" />
                <input value={e.title || ""} onChange={(ev) => setEmp(i, "title", ev.target.value)} placeholder="Title" className="w-36 border border-zinc-300 rounded px-2 py-1 text-xs outline-none focus:border-gold-dark" />
                <input value={e.email || ""} onChange={(ev) => setEmp(i, "email", ev.target.value)} placeholder="Email" className="flex-1 min-w-[160px] border border-zinc-300 rounded px-2 py-1 text-xs outline-none focus:border-gold-dark" />
                <input value={e.phone || ""} onChange={(ev) => setEmp(i, "phone", ev.target.value)} placeholder="Phone" className="w-28 border border-zinc-300 rounded px-2 py-1 text-xs outline-none focus:border-gold-dark" />
                <button onClick={() => removeEmp(i)} className="p-1.5 rounded bg-zinc-100 text-zinc-400 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            ) : (
              <div key={i} className="flex items-start justify-between gap-3 py-1.5 border-b border-zinc-50 last:border-0" data-testid={`employee-view-${i}`}>
                <div className="min-w-0">
                  <div className="text-sm text-zinc-900 flex items-center gap-1.5">
                    {e.name === "VACANT" ? <span className="text-amber-600 font-medium">VACANT</span> : e.name}
                    <span className="text-xs text-zinc-400">· {e.title}</span>
                  </div>
                  <div className="text-xs text-zinc-500 flex items-center gap-3 mt-0.5">
                    {e.email && <span className="flex items-center gap-1"><Mail className="h-3 w-3" /> {e.email}</span>}
                    {e.phone ? <span className="flex items-center gap-1"><Phone className="h-3 w-3" /> {e.phone}</span>
                      : (e.name !== "VACANT" && <span className="flex items-center gap-1 text-amber-600"><Phone className="h-3 w-3" /> phone MISSING</span>)}
                  </div>
                  {e.flag && <div className="text-[11px] text-amber-600 flex items-center gap-1 mt-0.5"><AlertTriangle className="h-3 w-3" /> {e.flag}</div>}
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
      )}

      {tab === "Commitments" && (
        <div className="mt-6" data-testid="commitments-panel">
          <CommitmentsCard isAdmin={isAdmin} />
        </div>
      )}

      {tab === "Company Documents" && (
        <div className="mt-6" data-testid="company-docs-panel">
          <CompanyDocs data={data} isAdmin={isAdmin} uploading={uploading} upload={upload}
            deleteDoc={deleteDoc} docCategory={docCategory} setDocCategory={setDocCategory} />
        </div>
      )}

      {tab === "Contracts" && (
        <div className="mt-6">
          <Contracts isOfficer={!!user} />
        </div>
      )}

      {/* Ingest confirmation modal */}
      {ingest && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 p-4" data-testid="ingest-modal" onClick={() => setIngest(null)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 bg-zinc-950 text-white rounded-t-xl">
              <div className="flex items-center gap-2.5"><span className="h-8 w-8 rounded-full bg-gold/20 flex items-center justify-center"><AnvilIcon className="h-5 w-5 text-gold" /></span>
                <div className="leading-tight"><div className="font-display font-bold">Anvil deciphered this doc</div><div className="text-[10px] text-zinc-400 font-mono">{ingest.filename}</div></div></div>
              <button onClick={() => setIngest(null)}><X className="h-5 w-5 text-zinc-400 hover:text-white" /></button>
            </div>
            <div className="p-5">
              <p className="text-sm text-zinc-600 mb-4">{ingest.proposal.summary || "Review and accept what to add. Nothing is saved until you apply."}</p>
              {Object.keys(ingest.proposal.fields || {}).length > 0 && (
                <div className="mb-4">
                  <div className="text-[11px] uppercase tracking-wider text-zinc-400 mb-1.5">Profile fields</div>
                  {Object.entries(ingest.proposal.fields).map(([k, v]) => (
                    <label key={k} className="flex items-center gap-2 py-1 text-sm" data-testid={`ingest-field-${k}`}>
                      <input type="checkbox" checked={!!ingest.accept.fields[k]} onChange={(e) => setIngest((g) => ({ ...g, accept: { ...g.accept, fields: { ...g.accept.fields, [k]: e.target.checked } } }))} />
                      <span className="text-zinc-500 w-32">{k}</span><span className="text-zinc-900 flex-1">{String(v)}</span>
                    </label>
                  ))}
                </div>
              )}
              {(ingest.proposal.custom_fields || []).length > 0 && (
                <div className="mb-4">
                  <div className="text-[11px] uppercase tracking-wider text-zinc-400 mb-1.5">Custom fields</div>
                  {ingest.proposal.custom_fields.map((c, i) => (
                    <label key={i} className="flex items-center gap-2 py-1 text-sm" data-testid={`ingest-custom-${i}`}>
                      <input type="checkbox" checked={!!ingest.accept.custom[i]} onChange={(e) => { const a = [...ingest.accept.custom]; a[i] = e.target.checked; setIngest((g) => ({ ...g, accept: { ...g.accept, custom: a } })); }} />
                      <span className="text-zinc-500">{c.section ? `${c.section} / ` : ""}{c.label}{c.sensitive ? " 🔒" : ""}:</span><span className="text-zinc-900 flex-1">{c.value}</span>
                    </label>
                  ))}
                </div>
              )}
              {ingest.proposal.about_addition && (
                <label className="flex items-start gap-2 py-1 text-sm" data-testid="ingest-about">
                  <input type="checkbox" className="mt-1" checked={ingest.accept.about} onChange={(e) => setIngest((g) => ({ ...g, accept: { ...g.accept, about: e.target.checked } }))} />
                  <span><span className="text-[11px] uppercase tracking-wider text-zinc-400 block mb-0.5">Add to About</span><span className="text-zinc-700">{ingest.proposal.about_addition}</span></span>
                </label>
              )}
            </div>
            <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-zinc-200">
              <button onClick={() => setIngest(null)} className="text-sm px-4 py-2 rounded-md bg-zinc-100 hover:bg-zinc-200 text-zinc-700">Skip</button>
              <button onClick={applyIngest} data-testid="ingest-apply-btn" className="flex items-center gap-1.5 text-sm px-4 py-2 rounded-md bg-emerald-600 hover:bg-emerald-500 text-white">
                <Check className="h-4 w-4" /> Apply accepted
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function CommitmentsCard({ isAdmin }) {
  const [rows, setRows] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try { const r = await api.get("/company/commitments"); setRows(r.data.commitments || []); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't load commitments."); }
  };
  useEffect(() => { load(); }, []);

  const setRow = (i, patch) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, ...patch } : r)));

  const save = async () => {
    setSaving(true);
    try {
      const commitments = Object.fromEntries(rows.map((r) => [r.field, { confirmed: !!r.confirmed, text: r.text }]));
      const res = await api.put("/company/commitments", { commitments });
      toast.success(`Saved — ${res.data.confirmed_count} commitment${res.data.confirmed_count === 1 ? "" : "s"} will ship in the technical volume.`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed."); }
    finally { setSaving(false); }
  };

  if (!rows) return <div className="text-sm text-zinc-400 font-mono py-8">Loading…</div>;
  const confirmedCount = rows.filter((r) => r.confirmed).length;

  return (
    <div data-testid="commitments-card" className="space-y-4">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="max-w-2xl">
          <h2 className="font-display font-semibold text-lg text-zinc-900">WD Standard Commitments</h2>
          <p className="text-sm text-zinc-500 mt-1">
            The perks that make WD stand out — confirm each one to ship it in every technical proposal.
            <span className="text-zinc-400"> Only confirmed commitments appear; unconfirmed ones are omitted entirely (never a placeholder).</span>
          </p>
          <p className="text-xs text-amber-700 mt-2">
            Do not add any day- or hour-count that the solicitation didn't set. Schedule items stay "per the solicitation."
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs font-mono text-zinc-500" data-testid="commitments-confirmed-count">{confirmedCount} / {rows.length} confirmed</span>
          {isAdmin && (
            <button onClick={save} disabled={saving} data-testid="commitments-save-btn"
              className="flex items-center gap-1.5 text-sm bg-emerald-600 text-white px-4 py-2 rounded-md hover:bg-emerald-500 disabled:opacity-50">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
            </button>
          )}
        </div>
      </div>

      <div className="space-y-2">
        {rows.map((r, i) => (
          <div key={r.field} data-testid={`commitment-row-${r.field}`}
            className={`rounded-lg border p-3 transition-colors ${r.confirmed ? "border-emerald-300 bg-emerald-50/40" : "border-zinc-200 bg-white"}`}>
            <div className="flex items-start gap-3">
              <button type="button" disabled={!isAdmin} onClick={() => setRow(i, { confirmed: !r.confirmed })}
                data-testid={`commitment-toggle-${r.field}`}
                className={`mt-0.5 h-5 w-5 shrink-0 rounded flex items-center justify-center border transition-colors ${
                  r.confirmed ? "bg-emerald-600 border-emerald-600 text-white" : "bg-white border-zinc-300 text-transparent"} ${isAdmin ? "cursor-pointer" : "cursor-default"}`}>
                <Check className="h-3.5 w-3.5" />
              </button>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-zinc-900">{r.label}</span>
                  {r.confirmed
                    ? <span className="text-[10px] uppercase tracking-wider text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded">Ships</span>
                    : <span className="text-[10px] uppercase tracking-wider text-zinc-400 bg-zinc-100 px-1.5 py-0.5 rounded">Not shipped</span>}
                </div>
                <textarea value={r.text} disabled={!isAdmin} rows={2}
                  data-testid={`commitment-text-${r.field}`}
                  onChange={(e) => setRow(i, { text: e.target.value })}
                  className="mt-1.5 w-full text-sm text-zinc-700 border border-zinc-200 rounded px-2 py-1.5 outline-none focus:border-gold-dark resize-y disabled:bg-zinc-50 disabled:text-zinc-500" />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}



const DOC_CATEGORIES = ["Capability Statement", "Certification", "License", "Insurance / COI",
  "Surety / Bonding", "W-9", "Past Performance", "Safety / OSHA", "Branding", "Other"];

function CompanyDocs({ data, isAdmin, uploading, upload, deleteDoc, docCategory, setDocCategory }) {
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("All");
  const docs = data.docs || [];
  const cats = ["All", ...Array.from(new Set(docs.map((d) => d.category).filter(Boolean)))];
  const shown = docs.filter((d) =>
    (cat === "All" || d.category === cat) &&
    (!q || (d.filename || "").toLowerCase().includes(q.toLowerCase())));

  return (
    <div className="bg-white border border-zinc-200 rounded-lg" data-testid="company-documents-card">
      <div className="px-4 py-3 border-b border-zinc-200 flex items-center justify-between flex-wrap gap-3">
        <span className="text-[11px] tracking-[0.1em] font-semibold text-zinc-500 uppercase">Company documents · Anvil ingests these into the KB</span>
        {isAdmin && (
          <div className="flex items-center gap-2">
            <select value={docCategory} onChange={(e) => setDocCategory(e.target.value)} data-testid="doc-category-select"
              className="border border-zinc-300 rounded-md px-2 py-1.5 text-xs text-zinc-700 outline-none focus:border-gold-dark">
              <option value="">Category…</option>
              {DOC_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <label className="flex items-center gap-1.5 text-sm bg-zinc-900 text-white px-3 py-1.5 rounded-md hover:bg-zinc-800 cursor-pointer" data-testid="upload-doc-label">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload
              <input type="file" accept=".md,.markdown,.txt,.pdf,.doc,.docx,.png,.jpg,.jpeg" className="hidden" data-testid="upload-doc-input"
                onChange={(e) => upload(e.target.files?.[0])} disabled={uploading} />
            </label>
          </div>
        )}
      </div>
      <div className="p-4">
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="h-4 w-4 text-zinc-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search documents…" data-testid="doc-search"
              className="w-full border border-zinc-300 rounded-md pl-8 pr-3 py-1.5 text-sm outline-none focus:border-gold-dark" />
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            {cats.map((c) => (
              <button key={c} onClick={() => setCat(c)} data-testid={`doc-cat-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${cat === c ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-600 border-zinc-200 hover:border-zinc-300"}`}>{c}</button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-2" data-testid="company-docs-list">
          {shown.length === 0 && <div className="text-xs text-zinc-400 py-4">No documents{q || cat !== "All" ? " match this filter" : " yet — upload certifications, COIs, surety letters, W-9, past performance, branding"}.</div>}
          {shown.map((d) => (
            <div key={d.id} className="flex items-center justify-between gap-2 border border-zinc-200 rounded-md px-3 py-2.5" data-testid={`company-doc-${d.id}`}>
              <div className="flex items-start gap-2 min-w-0">
                <FileText className="h-4 w-4 text-zinc-400 shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <div className="text-sm text-zinc-800 truncate flex items-center gap-1.5">{d.filename}{d.sensitive && <Lock className="h-3 w-3 text-emerald-600 shrink-0" />}</div>
                  <div className="text-[10px] text-zinc-400 font-mono flex items-center gap-2 flex-wrap">
                    {d.category && <span className="inline-flex items-center gap-1 text-zinc-500"><Tag className="h-3 w-3" />{d.category}</span>}
                    <span>{(d.text_len || 0).toLocaleString()} chars</span>
                    {d.uploaded_by && <span>· {d.uploaded_by}</span>}
                    {d.uploaded_at && <span>· {new Date(d.uploaded_at).toLocaleDateString()}</span>}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <a href={`${API_BASE}/company/documents/${d.id}/download`} target="_blank" rel="noreferrer" className="p-1.5 text-zinc-400 hover:text-zinc-900"><Download className="h-3.5 w-3.5" /></a>
                {isAdmin && <button onClick={() => deleteDoc(d.id)} data-testid={`doc-delete-${d.id}`} className="p-1.5 text-zinc-400 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const CONTRACT_TYPES = ["Awarded Contract", "MSA", "Teaming Agreement", "Subcontract", "NDA", "Other"];
const EMPTY_CONTRACT = { name: "", counterparty: "", contract_type: "Awarded Contract", value: "",
  start_date: "", end_date: "", status: "Active", notes: "" };
const STATUS_STYLE = { Active: "bg-emerald-100 text-emerald-700", Expired: "bg-zinc-200 text-zinc-600",
  Pending: "bg-amber-100 text-amber-700", Closed: "bg-zinc-200 text-zinc-600" };

function Contracts({ isOfficer }) {
  const [rows, setRows] = useState(null);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(EMPTY_CONTRACT);
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState("");

  const load = () => api.get("/company/contracts").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name.trim()) { toast.error("Contract name is required."); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v || ""));
      if (file) fd.append("file", file);
      await api.post("/company/contracts", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Contract added. Anvil now knows this teaming/award history.");
      setAdding(false); setForm(EMPTY_CONTRACT); setFile(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save."); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this contract?")) return;
    try { await api.delete(`/company/contracts/${id}`); toast("Contract removed."); load(); }
    catch { toast.error("Couldn't remove."); }
  };

  const shown = (rows || []).filter((c) => !q ||
    [c.name, c.counterparty, c.contract_type].some((x) => (x || "").toLowerCase().includes(q.toLowerCase())));

  return (
    <div className="bg-white border border-zinc-200 rounded-lg" data-testid="contracts-card">
      <div className="px-4 py-3 border-b border-zinc-200 flex items-center justify-between flex-wrap gap-3">
        <span className="text-[11px] tracking-[0.1em] font-semibold text-zinc-500 uppercase flex items-center gap-2"><ScrollText className="h-4 w-4" /> Contracts · awards, MSAs, teaming, subs, NDAs · feeds Anvil</span>
        {isOfficer && (
          <button onClick={() => setAdding((v) => !v)} data-testid="add-contract-btn" className="flex items-center gap-1.5 text-sm bg-zinc-900 text-white px-3 py-1.5 rounded-md hover:bg-zinc-800">
            <Plus className="h-4 w-4" /> Add contract
          </button>
        )}
      </div>

      {adding && (
        <div className="p-4 border-b border-zinc-200 bg-zinc-50" data-testid="contract-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <Field label="Name *"><input data-testid="contract-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="cf-input" /></Field>
            <Field label="Counterparty"><input data-testid="contract-counterparty" value={form.counterparty} onChange={(e) => setForm({ ...form, counterparty: e.target.value })} className="cf-input" /></Field>
            <Field label="Type">
              <select value={form.contract_type} onChange={(e) => setForm({ ...form, contract_type: e.target.value })} className="cf-input" data-testid="contract-type">
                {CONTRACT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </Field>
            <Field label="Value"><input value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} placeholder="$0" className="cf-input" /></Field>
            <Field label="Start date"><input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="cf-input" /></Field>
            <Field label="End date"><input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="cf-input" /></Field>
            <Field label="Status">
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="cf-input" data-testid="contract-status">
                {["Active", "Pending", "Expired", "Closed"].map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
            <Field label="File (optional)"><input type="file" onChange={(e) => setFile(e.target.files?.[0])} className="text-xs" data-testid="contract-file" /></Field>
            <Field label="Notes"><input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="cf-input" /></Field>
          </div>
          <div className="flex items-center justify-end gap-2 mt-3">
            <button onClick={() => { setAdding(false); setForm(EMPTY_CONTRACT); }} className="text-sm text-zinc-500 px-3 py-1.5 hover:text-zinc-800">Cancel</button>
            <button onClick={save} disabled={saving} data-testid="save-contract-btn" className="flex items-center gap-1.5 text-sm bg-emerald-600 text-white px-4 py-1.5 rounded-md hover:bg-emerald-500 disabled:opacity-50">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
            </button>
          </div>
        </div>
      )}

      <div className="p-4">
        <div className="relative mb-3 max-w-sm">
          <Search className="h-4 w-4 text-zinc-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search contracts…" data-testid="contract-search"
            className="w-full border border-zinc-300 rounded-md pl-8 pr-3 py-1.5 text-sm outline-none focus:border-gold-dark" />
        </div>
        {rows === null ? <div className="text-xs text-zinc-400 py-4 font-mono">Loading…</div> : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="contracts-list">
            {shown.length === 0 && <div className="text-xs text-zinc-400 py-4">No contracts yet. Add awarded contracts, MSAs, teaming agreements, subcontracts and NDAs so Anvil can cite past performance.</div>}
            {shown.map((c) => (
              <div key={c.id} className="border border-zinc-200 rounded-lg p-3.5" data-testid={`contract-${c.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-zinc-900 truncate">{c.name}</div>
                    <div className="text-xs text-zinc-500">{c.counterparty || "—"}</div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {c.storage_path && <a href={`${API_BASE}/company/contracts/${c.id}/download`} target="_blank" rel="noreferrer" className="p-1 text-zinc-400 hover:text-zinc-900"><Download className="h-3.5 w-3.5" /></a>}
                    {isOfficer && <button onClick={() => remove(c.id)} data-testid={`contract-delete-${c.id}`} className="p-1 text-zinc-400 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>}
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-2 flex-wrap text-[11px]">
                  {c.contract_type && <span className="bg-zinc-100 text-zinc-600 px-2 py-0.5 rounded-sm">{c.contract_type}</span>}
                  {c.value && <span className="font-mono text-zinc-900">{c.value}</span>}
                  <span className={`px-2 py-0.5 rounded-full font-semibold ${STATUS_STYLE[c.status] || "bg-zinc-100 text-zinc-600"}`}>{c.status}</span>
                </div>
                {(c.start_date || c.end_date) && (
                  <div className="text-[11px] text-zinc-400 font-mono mt-1.5 flex items-center gap-1"><Calendar className="h-3 w-3" /> {c.start_date || "?"} → {c.end_date || "?"}</div>
                )}
                {c.notes && <div className="text-[11px] text-zinc-500 mt-1.5">{c.notes}</div>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <label className="block"><span className="text-[10px] uppercase tracking-wider text-zinc-400 block mb-1">{label}</span>{children}</label>
);
